import { cn } from '@rocco/utils/cn';
import type { UseEnquiryProps } from '~/modules/enquiry/hooks/use-enquiry';
import { getEnquiryGateStatement } from '~/modules-rocco/enquiry/enquiry-gate-copy';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import {
    GATE_FIELD_INPUT_CLASS,
    GATE_FIELD_LABEL_CLASS,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { GATE_PANE_BRAND_DEFAULT } from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { MINI_BODY_CLASS, MINI_PANE_CLASS } from './ModalPeek';

export interface EnquiryPeekSubject {
    /** The project's banner, behind the pane's statement. */
    image?: string;
    practice: string;
    /** The practice's own line under the statement, where the gate puts it. */
    note?: string | null;
    /**
     * What the real modal will say it is for. The statement comes from the
     * gate's own copy resolver with this, so the miniature's words are the
     * modal's words — a product's says "direct message" where a project's
     * does not, and a landing that reworded itself read as a jump.
     */
    itemType: UseEnquiryProps['itemType'];
}

/** A field as the gate draws it: label over an empty box with its prompt. */
const MiniField = ({
    label,
    placeholder,
    className,
}: {
    label: string;
    placeholder: string;
    className?: string;
}) => (
    <div className="flex flex-col gap-1.5">
        <p className={cn('m-0', GATE_FIELD_LABEL_CLASS)}>{label}</p>
        <div
            className={cn(
                GATE_FIELD_INPUT_CLASS,
                'flex items-center text-black/30',
                className,
            )}
        >
            {placeholder}
        </div>
    </div>
);

/**
 * The enquiry gate as it opens, drawn small for {@link ModalPeek}: the brand
 * pane naming the practice on the left, the empty form on the right.
 */
export const EnquiryMiniModal = ({
    subject,
}: {
    subject: EnquiryPeekSubject;
}) => (
    <>
        <div className={MINI_PANE_CLASS}>
            <AuthModalBrandPane
                statement={getEnquiryGateStatement(subject.itemType, {
                    professionalTitle: subject.practice,
                })}
                image={subject.image ?? GATE_PANE_BRAND_DEFAULT}
                note={subject.note ?? undefined}
            />
        </div>
        <div className={MINI_BODY_CLASS}>
            {/* max-w-90.5: the gate form's own column, measured off the real
                modal — a wider sketch put Continue's ends a step from where
                they land. */}
            <div className="mx-auto flex w-full max-w-90.5 flex-1 flex-col gap-5.5">
                <div className="grid grid-cols-2 gap-1.5">
                    <MiniField label="First Name" placeholder="First Name" />
                    <MiniField label="Last Name" placeholder="Last Name" />
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                    <MiniField
                        label="Email Address"
                        placeholder="you@email.com"
                    />
                    <MiniField label="Phone" placeholder="Phone number" />
                </div>
                <MiniField
                    label="Message"
                    placeholder="E.g. Hi there, we are planning a residential new build and are interested in working with you."
                    className="h-32.5 items-start py-2.5 leading-snug"
                />
                <div className="inline-flex h-[37px] w-fit items-center gap-1.5 rounded-[var(--radius-btn-m)] border border-black bg-white px-4 text-[12px] font-medium text-[#181818]">
                    Attach files
                </div>
                {/* -mb-3.5: the real footer is sticky and pulls itself below
                    the body's padding; without the same pull the sketched
                    Continue sat a step above where the real one lands. */}
                <div className="mt-auto -mb-3.5 border-t border-black/5 pt-4">
                    <div className="flex h-14 w-full items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3]">
                        Continue
                    </div>
                </div>
            </div>
        </div>
    </>
);
